import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Paper
} from '@mui/material';
import { DetailedReportData, ReportFilters as ReportFiltersType, JewelerReportData, MonthlyReportData } from '../utils/types';
import { getDetailedReports, getJewelerReports, getMonthlyReports, exportReportToExcel } from '../services/admin/AdminServices';
import { baseUrl, fetcher } from '../utils/global';
import useSWR from 'swr';
import DetailedReportCard from '../components/DetailedReportCard';
import ReportFilters from '../components/ReportFilters';
import { getBranches } from '../services/seller/SellerServices';
import MonthlyReportChart from '../components/MonthlyReportChart';
import JewelerReportTable from '../components/JewelerReportTable';
import ProductReport from '../components/ProductReport';
import ProductAnalysisChart from '../components/ProductAnalysisChart';
import { useAlert } from '../hooks/useAlert';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`report-tabpanel-${index}`}
      aria-labelledby={`report-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

export default function DetailedSellerReportContainer() {
  const { showError } = useAlert();
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState<DetailedReportData | null>(null);
  const [monthlyData, setMonthlyData] = useState<MonthlyReportData[]>([]);
  const [filters, setFilters] = useState<ReportFiltersType>({
    reportType: 'monthly',
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });
  const [branches, setBranches] = useState<Array<{ id: string; name: string }>>([]);

  // Mevcut API'den veri çek
  const { data: currentData } = useSWR(`${baseUrl}/reports`, fetcher);

  const fetchReportData = async (currentFilters: ReportFiltersType) => {
    setLoading(true);
    try {
      // Mock data oluştur (gerçek API'ler hazır olana kadar)
      const mockDetailedData: DetailedReportData = {
        totalRevenue: currentData?.totalRevenue || 0,
        totalProfit: currentData?.totalProfit || 0,
        totalLoss: 0, // API'de yok, hesaplanabilir
        monthlyBreakdown: [
          {
            month: "06",
            year: 2024,
            monthName: "Haziran",
            revenue: currentData?.totalRevenue || 0,
            profit: currentData?.totalProfit || 0,
            loss: 0,
            transactionCount: 76,
            averageTransactionValue: (currentData?.totalRevenue || 0) / 76,
            jewelerBreakdown: []
          }
        ],
        jewelerBreakdown: [],
        productSummary: currentData?.productSummary || {},
        dateRange: {
          startDate: currentFilters.startDate || new Date().toISOString().split('T')[0],
          endDate: currentFilters.endDate || new Date().toISOString().split('T')[0]
        }
      };

      setReportData(mockDetailedData);
      setMonthlyData(mockDetailedData.monthlyBreakdown);
    } catch (error) {
      showError('Rapor verileri yüklenirken hata oluştu');
      console.error('Error fetching report data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReportData(filters);
    // fetch branches for current seller
    (async () => {
      try {
        const res = await getBranches(0, 200, '');
        const items = (res?.results || res || []).map((b: any) => ({ id: b.id, name: b.name }));
        setBranches(items);
      } catch (e) {
        setBranches([]);
      }
    })();
  }, []);

  const handleFiltersChange = (newFilters: ReportFiltersType) => {
    setFilters(newFilters);
    fetchReportData(newFilters);
  };

  const handleExport = async (exportFilters: ReportFiltersType) => {
    try {
      await exportReportToExcel(exportFilters, 'seller');
    } catch (error) {
      showError('Rapor export edilirken hata oluştu');
      console.error('Error exporting report:', error);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const getReportCards = () => {
    if (!reportData) return [];

    return [
      {
        title: 'Toplam Ciro',
        value: reportData.totalRevenue,
        currency: 'TL',
        color: '#1976d2',
        trend: {
          value: 12.5,
          direction: 'up' as const
        }
      },
      {
        title: 'Toplam Kar',
        value: reportData.totalProfit,
        currency: 'TL',
        color: '#2e7d32',
        trend: {
          value: 8.3,
          direction: 'up' as const
        }
      },
      {
        title: 'Net Kar/Zarar',
        value: reportData.totalProfit - reportData.totalLoss,
        currency: 'TL',
        color: reportData.totalProfit - reportData.totalLoss >= 0 ? '#2e7d32' : '#d32f2f',
        trend: {
          value: 15.2,
          direction: 'up' as const
        }
      }
    ];
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography
        sx={{
          fontSize: '30px',
          fontWeight: '400',
          mb: 3
        }}
      >
        Detaylı Raporlar
      </Typography>

      <ReportFilters
        onFiltersChange={handleFiltersChange}
        onExport={handleExport}
        loading={loading}
        hideJeweler
        branches={branches}
      />



      {/* Tabs */}
      <Paper sx={{ width: '100%' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="report tabs">
            <Tab label="Genel Bakış" />
            <Tab label="Aylık Analiz" />
            <Tab label="Mağaza Detayları" />
            <Tab label="Ürün Analizi" />
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <MonthlyReportChart 
                data={monthlyData} 
                chartType="line"
                showJewelerBreakdown={false}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                    Özet Bilgiler
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">
                        Toplam Mağaza:
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {reportData?.jewelerBreakdown[0]?.branches?.length || 0}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">
                        Toplam İşlem:
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {reportData?.jewelerBreakdown[0]?.transactionCount || 0}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">
                        Ortalama İşlem Değeri:
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {reportData?.jewelerBreakdown[0]?.averageTransactionValue?.toFixed(2) || 0} TL
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <MonthlyReportChart 
            data={monthlyData} 
            chartType="bar"
            showJewelerBreakdown={false}
          />
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          {reportData?.jewelerBreakdown[0] && (
            <JewelerReportTable 
              data={[reportData.jewelerBreakdown[0]]}
              showBranches={true}
            />
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={3}>
          {reportData && (
            <Box>
              <ProductReport products={reportData.productSummary} />
              
              {/* Advanced Product Analysis */}
              <Box sx={{ mt: 3 }}>
                <ProductAnalysisChart 
                  products={reportData.productSummary} 
                  chartType="bar"
                  showTopProducts={10}
                />
              </Box>
            </Box>
          )}
        </TabPanel>
      </Paper>
    </Box>
  );
}
